import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Theme,
  Typography, ListItemButton } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { OutlinedButton } from 'components/OutlinedButton';
import { Notifications } from '../types';
import { ConfirmationModal } from '../hooks/email-templates-hooks';

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      padding: theme.spacing(2),
    },
    header: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    title: {
      flex: '1 1 auto',
      minWidth: 0,
      overflowWrap: 'anywhere',
      textTransform: 'none',
    },
    addButton: {
      flex: '0 0 auto',
      width: theme.spacing(3.5),
      height: theme.spacing(3.5),
      minWidth: 0,
      padding: 0,
      [COARSE_POINTER]: {
        width: theme.spacing(5.5),
        height: theme.spacing(5.5),
      },
    },
    body: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    list: {
      minWidth: 0,
    },
    row: {
      minWidth: 0,
      [COARSE_POINTER]: { minHeight: theme.spacing(5.5) },
    },
    // Reserve room for the absolutely positioned delete action so it never covers the subject.
    rowWithAction: {
      paddingRight: theme.spacing(8),
    },
    rowText: {
      minWidth: 0,
    },
    subject: {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 3,
      overflow: 'hidden',
      overflowWrap: 'anywhere',
      minWidth: 0,
    },
    deleteButton: {
      [COARSE_POINTER]: {
        minWidth: theme.spacing(5.5),
        minHeight: theme.spacing(5.5),
      },
    },
    deleteIcon: {
      color: theme.palette.error.main,
    },
    state: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(3, 1),
      textAlign: 'center',
      overflowWrap: 'anywhere',
      minWidth: 0,
      '& .MuiButton-root': {
        [COARSE_POINTER]: { minHeight: theme.spacing(5.5) },
      },
    },
  })
);

type ListTemplatesProps = {
  notifications: Notifications[];
  selectedNotification: Notifications | undefined;
  setSelectedNotification: React.Dispatch<React.SetStateAction<Notifications | undefined>>;
  setConfirmationModal: React.Dispatch<React.SetStateAction<ConfirmationModal>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

const ListTemplates: FC<ListTemplatesProps> = ({
  notifications,
  selectedNotification,
  setSelectedNotification,
  setConfirmationModal,
  setOpen,
  isLoading,
  isError,
  onRetry,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box className={classes.state} role="status" aria-busy="true">
          <CircularProgress size={24} color="secondary" aria-busy="true" />
          <Typography variant="body2">{t('emailTemplates:loading')}</Typography>
        </Box>
      );
    }
    if (isError) {
      return (
        <Box className={classes.state} role="alert">
          <Typography variant="body2">{t('emailTemplates:load-error')}</Typography>
          <OutlinedButton text={t('emailTemplates:retry')} onClick={onRetry} />
        </Box>
      );
    }
    if (notifications.length === 0) {
      return (
        <Box className={classes.state}>
          <Typography variant="body2" color="textSecondary">
            {t('emailTemplates:no-templates')}
          </Typography>
        </Box>
      );
    }
    return (
      <List className={classes.list}>
        {notifications.map((notification) => (
          <ListItem
            disablePadding
            key={notification._id}
            className={classes.row}
          >
            <ListItemButton
              selected={selectedNotification?._id === notification._id}
              onClick={() => setSelectedNotification(notification)}
              className={classes.rowWithAction}
            >
              <ListItemText
                className={classes.rowText}
                primary={notification.subject}
                primaryTypographyProps={{ className: classes.subject, title: notification.subject }}
              />
            </ListItemButton>
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                className={classes.deleteButton}
                aria-label={t('emailTemplates:delete-template')}
                onClick={() => {
                  setConfirmationModal({ openConfirmation: true, id: notification._id });
                }}
                size="large"
              >
                <FontAwesomeIcon icon="trash" size="xs" className={classes.deleteIcon} />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Paper className={classes.paper}>
      <Box className={classes.header}>
        <Typography variant="button" className={classes.title}>
          {t('emailTemplates:template-list')}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          className={classes.addButton}
          aria-label={t('emailTemplates:add-template')}
          onClick={() => setOpen(true)}
        >
          <FontAwesomeIcon icon="plus" />
        </Button>
      </Box>

      <Box className={classes.body} aria-busy={isLoading}>
        {renderBody()}
      </Box>
    </Paper>
  );
};

export default ListTemplates;
